<script setup lang="ts">
/**
 * The drawing surface — raster revision.
 *
 * Sketch layers are 512² bitmaps: rendering each tile placement is one
 * clipped drawImage per layer — fast and smooth at any stroke count.
 * Trace layers are vector Path2Ds in unconstrained tile-space, drawn unclipped
 * (once, as designed — not tiled) so a traced shape spills exactly as drawn.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';
import { select } from 'd3-selection';
import { zoom, zoomIdentity, type D3ZoomEvent, type ZoomTransform } from 'd3-zoom';
import { compose, rotationAbout, scale as scaleMat, transformToMatrix, translation, isIdentityTransform, type Mat } from '@/geometry/affine';
import { outsetPolygon } from '@/geometry/wrap';
import { rotationStepRadians } from '@/patterns/wallpaper/fundamental';
import { planLayerPlacement } from '@/services/layerPlacementBitmap';
import { tintCanvas } from '@/services/rasterTint';
import { CANVAS_ROTATION_ENABLED, useCanvasStore } from '@/stores/canvas';
import { usePatternStore } from '@/stores/pattern';
import { TILE_RASTER, useLayersStore, type SketchLayerMeta } from '@/stores/layers';
import { useToolsStore } from '@/stores/tools';
import { useUiStore } from '@/stores/ui';
import { useDrawing } from '@/composables/useDrawing';
import { createPressureReliabilityTracker, type PenInput } from '@/composables/penDynamics';
import { useTraceEditor } from '@/composables/useTraceEditor';
import { useWrapTransform } from '@/composables/useWrapTransform';
import type { TileTransform } from '@/patterns/types';
import type { TraceLayerData } from '@/types/drawing';
import TraceFabs from '@/components/canvas/TraceFabs.vue';

const canvasEl = ref<HTMLCanvasElement | null>(null);
const wrapperEl = ref<HTMLDivElement | null>(null);

const canvasStore = useCanvasStore();
const patternStore = usePatternStore();
const layers = useLayersStore();
const tools = useToolsStore();
const ui = useUiStore();
const { visibleTransforms, visibleCellTransforms, screenToWorld, unrotateScreenDelta } = useWrapTransform();
const drawing = useDrawing();
const trace = useTraceEditor();

let dpr = 1;
let rafId = 0;
let timerId: ReturnType<typeof setTimeout> | null = null;
let needsDraw = false;

// §6: max zoom raised for pixel-level tracing precision (was 10). At the default
// tile size (120px) this is ≈9 device-px per 512-raster-pixel — solidly
// pixel-level; even smaller tiles zoom in further per raster pixel.
const MAX_ZOOM = 40;
/** min zoom (k0*0.4), set once the initial fit is known. */
const zoomMin = ref(0.1);
/** set in onMounted — drives the zoom slider through d3 (which owns the transform). */
let applyScaleTo: ((k: number) => void) | null = null;
/** Zoom slider position 0..1, log-scaled between [zoomMin, MAX_ZOOM]. */
const zoomPct = computed<number>({
  get: () => {
    const k = canvasStore.viewTransform.k;
    const lo = zoomMin.value;
    if (k <= lo) return 0;
    return Math.min(1, Math.log(k / lo) / Math.log(MAX_ZOOM / lo));
  },
  set: (frac) => {
    const lo = zoomMin.value;
    applyScaleTo?.(lo * Math.pow(MAX_ZOOM / lo, Math.max(0, Math.min(1, frac))));
  },
});

// keyed by layer id + d, so a Path2D is rebuilt only when the shape changes
const path2dCache = new Map<string, { key: string; path: Path2D }>();

function getPath2D(l: TraceLayerData): Path2D {
  const key = `${l.id}:${l.d}`;
  const cached = path2dCache.get(l.id);
  if (cached && cached.key === key) return cached.path;
  const path = new Path2D(l.d);
  path2dCache.set(l.id, { key, path });
  return path;
}

// ---- colour tiling (brief §4): per-placement recolour ----
//
// Colour travels with the stroke: sketch layers are graphite/glow and NEVER
// tinted; ink layers keep one raster per origin-class and, at a placement of
// class j, the raster for class k is tinted to colours[(j-k) mod n] — so a
// mark keeps the ink colour in the cell it was drawn in and its symmetry
// copies shift. `planLayerPlacement` (services/layerPlacementBitmap.ts) is the
// shared decision, so the live canvas and the offscreen export renderer can't
// drift. Tints are cached per (raster, hex), invalidated by layers.revision,
// since this path redraws every frame.
const recolourCache = new Map<string, { revision: number; canvas: HTMLCanvasElement }>();
/** Per-frame tint cache for the actively drawn/erased ink layer (cleared at the
 *  top of each draw()); tints once per colour and reused across placements. */
const liveTintCache = new Map<string, HTMLCanvasElement>();

function getRecoloured(key: string, canvas: HTMLCanvasElement, hex: string, revision: number): HTMLCanvasElement {
  const cached = recolourCache.get(key);
  if (cached && cached.revision === revision) return cached.canvas;
  const c = tintCanvas(canvas, hex);
  recolourCache.set(key, { revision, canvas: c });
  return c;
}

/** Blit one layer at one placement into `ctx` (already positioned + clipped),
 *  composing per-origin-class rasters for authored ink, and (in coloured mode)
 *  tinting sketch per placement-class with the muted sketch palette (§4). */
function blitLayer(ctx: CanvasRenderingContext2D, layer: SketchLayerMeta, t: TileTransform): void {
  // planLayerPlacement handles all kinds: sketch (per-origin-class muted / grey),
  // ink (per-group per-class / imported), each as a list of tinted blits.
  const live = drawing.drawingActive.value && layer.id === layers.activeRasterLayer.id;
  const blits = planLayerPlacement(layer.id, t, patternStore.currentConfig.wpgId, tools.colourCount, tools.colours);
  // The revision-keyed tint cache only bumps on commit, so the layer being
  // actively drawn/erased would show a frozen tint until the gesture ends. For
  // it, tint through the per-frame liveTintCache: fresh every frame but tinted
  // once per colour and reused across all placements (not re-tinted per
  // placement, which made it laggy).
  for (const b of blits) {
    let img: HTMLCanvasElement;
    if (!b.hex) {
      img = b.canvas;
    } else if (live) {
      img = liveTintCache.get(b.key) ?? tintCanvas(b.canvas, b.hex);
      liveTintCache.set(b.key, img);
    } else {
      img = getRecoloured(b.key, b.canvas, b.hex, layers.revision);
    }
    ctx.drawImage(img, 0, 0, TILE_RASTER, TILE_RASTER, 0, 0, 1, 1);
  }
}

function css(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function scheduleDraw(): void {
  needsDraw = true;
}

function renderLoop(): void {
  if (needsDraw) {
    needsDraw = false;
    draw();
  }
  // rAF pauses entirely in hidden/occluded documents — timer fallback.
  if (typeof document !== 'undefined' && document.hidden) {
    timerId = setTimeout(renderLoop, 66);
  } else {
    rafId = requestAnimationFrame(renderLoop);
  }
}

function draw(): void {
  const canvas = canvasEl.value;
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Rebuild any sketch-layer canvas region touched since the last frame.
  // Deferred here (once per animation frame) rather than done per touch
  // sample — see useDrawing's dirty-rect + flushDirty pattern.
  drawing.flushDirty();

  // Per-frame tint cache for the live (actively drawn/erased) ink layer, whose
  // rasters change every frame so the revision-keyed getRecoloured cache can't
  // serve it. Cleared each frame; tints once per distinct colour and is reused
  // across every placement (the ~dozens of visible tiles), instead of the old
  // per-placement re-tint that made live ink laggy.
  liveTintCache.clear();

  const w = canvasStore.canvasWidth;
  const h = canvasStore.canvasHeight;
  const view = canvasStore.viewTransform;
  const ts = patternStore.tileSize;
  const polygon = patternStore.currentPattern.polygon();
  // All raster layers in stack order (sketch bottom → ink top); each carries
  // its own kind, so blitLayer tints ink and leaves sketch untouched.
  const visibleLayers = layers.rasterLayers.filter((l) => l.visible && l.opacity > 0);
  const ghostFactor = tools.showGhosts ? tools.ghostOpacity : 0;
  const traceLayers = layers.traceLayers;
  const editingId = layers.editingTraceLayerId;
  const inkPreviewD = layers.activeLayer === 'trace' ? trace.previewD() : '';

  // cache theme colours once per frame
  const colBg = css('--canvas-bg') || '#14121b';
  const colTileActive = css('--tile-fill-active') || '#1c1925';
  const colTileGhost = css('--tile-fill-ghost') || 'rgba(95,80,120,0.07)';
  const colBorderActive = css('--tile-border-active') || '#8d7ac2';
  const colBorderGhost = css('--tile-border-ghost') || '#393146';
  // §1: the trace tool's working overlays (in-progress outline, nodes/handles,
  // snap ghost) are chrome, not artwork — a fixed bright red that reads clearly
  // over any artwork in either theme, deliberately NOT the muted themed accent.
  const colTool = '#ff2d2d';
  // §5: candidate/new snap points use a distinct non-red colour so "already
  // placed" (red X) reads differently from "tap here to place" — a teal that
  // stays legible over muted sketch/ink tones without being garish.
  const colCandidate = '#13b6c9';
  // §4: a green "snap to close" cue for the lattice point that lands on the
  // start node — finishing the shape reads distinctly from continuing it.
  const colClose = '#2fce7c';
  // 08a: active-path highlight — bright green (user: cyan wasn't bright enough)
  const colPath = '#2fff5a';

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = colBg;
  ctx.fillRect(0, 0, w, h);

  const scale = view.k * ts;

  // Bug 5 (sketch tile seams): the layer raster is already seamless at tile
  // edges (useDrawing.ts duplicates every near-edge mark through the ring-1
  // symmetry transforms), but clipping two adjacent placements to polygons
  // that exactly abut leaves a Canvas2D clip-antialiasing seam — each side
  // can rasterise its shared boundary pixel at less than full coverage, so
  // the two halves don't sum back to opaque. Outsetting the clip polygon by
  // a sub-pixel amount makes neighbouring placements overlap slightly
  // instead of abutting exactly; since the content in that overlap is
  // identical either side, the extra overlap is invisible and the seam
  // closes. All wallpaper-group ops are isometries (no non-uniform scaling),
  // so one outset in tile-space gives the same device-pixel margin for
  // every placement — computed once per frame, not per transform.
  const CLIP_OUTSET_PX = 0.75;
  const clipPolygon = outsetPolygon(polygon, CLIP_OUTSET_PX / (dpr * scale));

  // Feature 1 (canvas rotation): a pure view transform layered on top of the
  // existing world→screen mapping, pivoted about the viewport centre (same
  // pivot useWrapTransform.ts's screenToWorld/worldToScreen use, so hit-testing
  // and drawing agree). Built with the same affine helpers those use, rather
  // than by hand, so the two can't drift apart.
  const rotation = canvasStore.rotation;
  const rotateAboutCentre = rotation !== 0 ? rotationAbout(rotation, w / 2, h / 2) : null;

  const setPlacement = (m: number[]): void => {
    // world → unrotated screen (translate+scale, exactly as before) → rotated
    // screen (Feature 1) → device pixels (dpr).
    let screenMat = compose(translation(view.x, view.y), compose(scaleMat(scale, scale), m as Mat));
    if (rotateAboutCentre) screenMat = compose(rotateAboutCentre, screenMat);
    const final = compose(scaleMat(dpr, dpr), screenMat);
    ctx.setTransform(final[0], final[1], final[2], final[3], final[4], final[5]);
  };

  // Tile backgrounds — once per lattice cell. For an op-invariant artist tile
  // (the hexagon) the point-group ops map the cell onto itself, so drawing the
  // fill per transform would alpha-stack N copies and darken it. The cell set
  // collapses those out; for ordinary variants it equals visibleTransforms.
  for (const t of visibleCellTransforms.value) {
    const m = transformToMatrix(t);
    const active = isIdentityTransform(t);
    setPlacement(m);
    ctx.beginPath();
    polygonPath(ctx, polygon);
    ctx.fillStyle = active ? colTileActive : colTileGhost;
    ctx.fill();
  }

  // Sketch/ink — clipped blit per transform (the in-tile kaleidoscope copies).
  for (const t of visibleTransforms.value) {
    const m = transformToMatrix(t);
    const active = isIdentityTransform(t);
    const factor = active ? 1 : ghostFactor;
    if (factor <= 0) continue;

    setPlacement(m);

    // sketch + ink layers — one clipped blit each (raster is edge-duplicated, so
    // the clip is seamless; outset slightly to avoid a clip-AA seam — Bug 5).
    // Ink expands to one blit per origin-class, tinted per placement. Live ink
    // strokes paint straight onto their class rasters, so they appear here with
    // no separate preview pass.
    ctx.save();
    ctx.beginPath();
    polygonPath(ctx, clipPolygon);
    ctx.clip();
    for (const layer of visibleLayers) {
      ctx.globalAlpha = Math.min(1, layer.opacity * factor);
      blitLayer(ctx, layer, t);
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  // In-progress trace preview (rubber-band) + its live symmetry ghosts (§1/§3).
  // Its OWN pass over the full transform set — which already includes
  // reflections/glides (det<0 explicit matrices) — deliberately decoupled from
  // the sketch ghost-tile toggle (`ghostFactor`), so every symmetry copy of the
  // line previews regardless of `showGhosts`. The old code drew this inside the
  // sketch/ink loop behind `if (factor <= 0) continue`, so the ghosts (most
  // visibly the mirrored copies in adjacent cells) vanished whenever ghost tiles
  // were off. Identity tile at full strength; other copies secondary but clearly
  // visible (was a near-invisible 0.35).
  if (inkPreviewD) {
    const previewPath = new Path2D(inkPreviewD);
    ctx.strokeStyle = colTool;
    ctx.lineWidth = Math.max(0.008, 1.6 / scale);
    for (const t of visibleTransforms.value) {
      setPlacement(transformToMatrix(t));
      ctx.globalAlpha = isIdentityTransform(t) ? 0.95 : 0.5;
      ctx.stroke(previewPath);
    }
    ctx.globalAlpha = 1;
  }

  // trace layers (brief 12-vector_draw): 'outline' mode (default, the
  // original behaviour) draws once in identity space (untiled, as drawn).
  // 'solid' mode instead replicates through every active symmetry
  // placement, unclipped — same transform set and identity-at-full-
  // strength/copies-secondary-alpha rule as the rubber-band preview above,
  // since a solid trace is meant to read as part of the tessellation, not a
  // one-off annotation. The draft being drawn is skipped either way and
  // shown via that preview instead.
  if (traceLayers.length > 0) {
    const isDraft = (l: TraceLayerData): boolean => l.id === editingId && trace.mode.value === 'draw';
    const solidLayers = traceLayers.filter(
      (l) => l.drawMode === 'solid' && l.visible && l.opacity > 0 && l.d && !isDraft(l),
    );
    if (solidLayers.length > 0) {
      for (const t of visibleTransforms.value) {
        setPlacement(transformToMatrix(t));
        for (const l of solidLayers) drawTraceLayer(ctx, l, scale, true);
      }
    }

    setPlacement([1, 0, 0, 1, 0, 0]);
    for (const l of traceLayers) {
      if (l.drawMode === 'solid') continue;
      if (!l.visible || l.opacity <= 0 || !l.d) continue;
      if (isDraft(l)) continue;
      drawTraceLayer(ctx, l, scale);
    }
  }

  // borders on top — once per cell (matches the background placement)
  for (const t of visibleCellTransforms.value) {
    const m = transformToMatrix(t);
    const active = isIdentityTransform(t);
    setPlacement(m);
    ctx.beginPath();
    polygonPath(ctx, polygon);
    ctx.lineWidth = (active ? 1.5 : 0.5) / scale;
    ctx.setLineDash(active ? [4 / scale, 4 / scale] : []);
    ctx.strokeStyle = active ? colBorderActive : colBorderGhost;
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // trace editor overlay (nodes + handles) — identity placement only
  const editing = layers.editingTraceLayer;
  if (layers.activeLayer === 'trace' && editing) {
    setPlacement([1, 0, 0, 1, 0, 0]);
    const inEdit = trace.mode.value === 'edit';
    // Touch-first: nodes/handles are chunky finger targets (§1 — draw-mode
    // markers enlarged too so snap targets are easy to see and hit on iPad).
    const r = (inEdit ? 6 : 5) / scale;
    // §1 marker sizing (device-px ÷ scale). Placed nodes are the LARGEST — solid
    // red dots. Snap targets are '+' glyphs: small by default, enlarged when they
    // lie on an active highlighted path (still smaller than a placed dot). Glyph
    // size is independent of the snap HIT radius (SNAP_PX, in useTraceEditor).
    const rDot = 6.5 / scale; // placed-node solid dot (largest)
    const rPlusSmall = 4.5 / scale; // default snap-target '+'
    const rPlusBig = 6 / scale; // '+' on an active path (< dot)
    const rm = 8.5 / scale; // ring sizing for selection / close cues
    /** A placed-node marker: a solid dot with a bg halo. */
    const drawDot = (x: number, y: number, radius: number, fill = colTool): void => {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = fill;
      ctx.fill();
      ctx.lineWidth = 2 / scale;
      ctx.strokeStyle = colBg;
      ctx.stroke();
    };
    /** A snap-target marker: a '+' with a bg halo (contrast-independent). The
     *  halo always stays `colBg`; only the glyph stroke takes `colour`, so
     *  on-path markers (colPath) still read against any background. */
    const drawSnapPlus = (x: number, y: number, half: number, colour = colCandidate): void => {
      for (const [col, lw] of [
        [colBg, 3 / scale],
        [colour, 1.8 / scale],
      ] as const) {
        ctx.strokeStyle = col;
        ctx.lineWidth = lw;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(x - half, y);
        ctx.lineTo(x + half, y);
        ctx.moveTo(x, y - half);
        ctx.lineTo(x, y + half);
        ctx.stroke();
      }
    };
    const showHandles = inEdit && trace.subMode.value === 'handles';
    const nodes = editing.nodes;

    // §1/§2: always-on snap-target '+' markers — every global snap target (nodes
    // on any trace + unit-cell corners), expanded across the viewport. Shown at
    // all times in draw mode, not just when tracing near them. Drawn first so the
    // active-path highlights and placed dots sit on top.
    if (!inEdit) {
      for (const [mx, my] of trace.snapMarkers()) drawSnapPlus(mx, my, rPlusSmall);
    }

    // Brief 08a: active-node path previews — "tap along this bright-green
    // walk". Each path follows the drawn-edge graph (it bends, unlike the old
    // straight lattice rays) and stops at a junction/endpoint; its vertices
    // render as enlarged '+' markers in the path colour, and a green ring cues
    // closing when a vertex reaches the start node.
    if (!inEdit) {
      const closeR = 0.035; // matches useTraceEditor CLOSE_RADIUS
      const start = nodes[0];
      const canClose = !!start && nodes.length > 2;
      const active = nodes.find((n) => n.id === trace.activeNodeId.value);
      if (active) {
        for (const path of trace.pathPreviews()) {
          if (path.points.length === 0) continue;
          ctx.beginPath();
          ctx.moveTo(active.x, active.y);
          for (const [px, py] of path.points) ctx.lineTo(px, py);
          ctx.strokeStyle = colPath;
          ctx.globalAlpha = 0.85;
          ctx.lineWidth = 2 / scale;
          ctx.stroke();
          ctx.globalAlpha = 1;
          for (const [px, py] of path.points) {
            if (canClose && Math.hypot(px - start!.x, py - start!.y) < closeR) {
              ctx.beginPath();
              ctx.arc(px, py, rm * 1.4, 0, Math.PI * 2);
              ctx.strokeStyle = colClose;
              ctx.lineWidth = 2.5 / scale;
              ctx.stroke();
            } else {
              drawSnapPlus(px, py, rPlusBig, colPath);
            }
          }
        }
      }
    }

    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i]!;
      const selected = a.id === trace.selectedNodeId.value;
      // handles (edit + handle sub-mode only)
      if (showHandles) {
        for (const h of [
          { x: a.x + a.outX, y: a.y + a.outY, on: a.outX !== 0 || a.outY !== 0 },
          { x: a.x + a.inX, y: a.y + a.inY, on: a.inX !== 0 || a.inY !== 0 },
        ]) {
          if (!h.on) continue;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(h.x, h.y);
          ctx.strokeStyle = colTool;
          ctx.lineWidth = 2 / scale;
          ctx.stroke();
          // filled disc with a bg halo so it pops against the stroke
          ctx.beginPath();
          ctx.arc(h.x, h.y, r * 0.95, 0, Math.PI * 2);
          ctx.fillStyle = colTool;
          ctx.fill();
          ctx.lineWidth = 2 / scale;
          ctx.strokeStyle = colBg;
          ctx.stroke();
        }
      }
      // §1: placed vertices are solid red dots. A selected node gets a larger
      // disc with a light core so selection reads; the first draw node (the
      // "close here" target) gets a ring cue around its dot.
      if (selected) {
        drawDot(a.x, a.y, rDot * 1.35);
        drawDot(a.x, a.y, rDot * 0.5, colBg); // light core marks the selection
      } else {
        if (i === 0 && !inEdit) {
          ctx.beginPath();
          ctx.arc(a.x, a.y, rDot * 1.8, 0, Math.PI * 2);
          ctx.strokeStyle = colTool;
          ctx.lineWidth = 2 / scale;
          ctx.stroke();
        }
        drawDot(a.x, a.y, rDot);
      }
    }

    // symmetry-snap preview while drawing (in range of the pointer — mouse
    // hover or an Apple Pencil hovering, not just an active drag/touch, since
    // trace.onMove runs on every pointermove regardless of button state): the
    // source twin is an existing (placed) node → red dot; the ghost the next
    // tap yields is a candidate → a ringed, larger teal '+' so the exact node
    // that's about to be snapped to reads unambiguously against the small
    // always-on markers around it.
    const sp = trace.snapPreview.value;
    if (!inEdit && sp) {
      drawDot(sp.sourceX, sp.sourceY, rDot);
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, rm * 1.2, 0, Math.PI * 2);
      ctx.strokeStyle = colCandidate;
      ctx.lineWidth = 2 / scale;
      ctx.stroke();
      drawSnapPlus(sp.x, sp.y, rPlusBig);
    }
  }
}

// Final-render outline weight, in actual on-screen pixels — deliberately NOT
// `l.strokeWidth` (a tile-space fraction that would look thicker/thinner as
// the canvas zooms instead of a crisp, zoom-independent line). `scale` is the
// caller's tile-unit→pixel factor (view.k * tileSize), so dividing by it here
// gives exactly this many CSS pixels regardless of zoom (matches the overlay
// glyphs' own `/scale` convention elsewhere in this file).
const FINAL_TRACE_STROKE_PX = 2;

/** `solid` (brief 12-vector_draw's Solid Vector Line mode) skips the fill —
 *  "simple solid strokes" — regardless of `l.fill`; otherwise identical. */
function drawTraceLayer(ctx: CanvasRenderingContext2D, l: TraceLayerData, scale: number, solid = false): void {
  if (l.stroke === 'none' || !l.d) return;
  const path = getPath2D(l);
  ctx.globalAlpha = l.opacity;
  // §5: a closed, committed trace gets a faint fill of its hue so bordering
  // regions read as separate. Absent on drafts/legacy layers ⇒ stroke-only.
  if (!solid && l.closed && l.fill) {
    ctx.fillStyle = l.fill;
    ctx.fill(path);
  }
  ctx.strokeStyle = l.stroke;
  ctx.lineWidth = FINAL_TRACE_STROKE_PX / scale;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke(path);
  ctx.globalAlpha = 1;
}

function polygonPath(ctx: CanvasRenderingContext2D, polygon: [number, number][]): void {
  ctx.moveTo(polygon[0]![0], polygon[0]![1]);
  for (let i = 1; i < polygon.length; i++) ctx.lineTo(polygon[i]![0], polygon[i]![1]);
  ctx.closePath();
}

// ---- pointer input (single pointer draws/erases/places; 2+ = gesture) ----
const activePointers = new Set<number>();
type Gesture = 'pen' | 'erase' | 'trace' | null;
let gesture: Gesture = null;

// Feature 1 (canvas rotation) — two-finger rotate, alongside d3-zoom's own
// two-finger pan/zoom (both read the same raw pointers; d3-zoom computes
// scale+translate from them independently, this just adds the angle term
// d3-zoom's ZoomTransform has no room for). Screen-space positions only —
// this never touches artwork or symmetry state, purely the view.
const pointerPositions = new Map<number, [number, number]>();
let rotateGesture: { startAngle: number; startRotation: number } | null = null;

// Feature (brief 14-even_closer): multi-finger tap → history. Two-finger tap =
// Undo, three-finger tap = Redo. A "tap" is a quick multi-finger press-and-lift
// with negligible movement; any real movement (pan / pinch / rotate) disqual-
// ifies it. Peak finger count is tracked because fingers seldom land or lift
// on the same frame, and each pointer's start position is kept so movement can
// be measured per finger.
const TAP_MAX_MS = 350;
const TAP_MAX_MOVE_PX = 12;
let multiTap: { maxCount: number; startTime: number; moved: boolean; starts: Map<number, [number, number]> } | null = null;

/** Fire the pending multi-finger tap once every finger has lifted. A no-op
 *  until the last pointer is up, or if the gesture moved / took too long. */
function finishMultiTap(): void {
  if (!multiTap || activePointers.size > 0) return;
  const { maxCount, startTime, moved } = multiTap;
  multiTap = null;
  if (moved || performance.now() - startTime > TAP_MAX_MS) return;
  if (maxCount === 2) layers.undo();
  else if (maxCount === 3) layers.redo();
}

/** Snap interval derives from the current drawing tile's actual shape (never
 *  hardcoded here) — a regular hexagon or equilateral triangle snaps at 60°,
 *  everything else at 45° — same shared step as the Tessellation panel's
 *  rotate buttons (`rotationStepRadians`). */
function snapRotation(rad: number): number {
  const step = rotationStepRadians(patternStore.currentPattern.polygon());
  const nearest = Math.round(rad / step) * step;
  const SNAP_PULL = (6 * Math.PI) / 180; // magnetic pull within ~6°
  return Math.abs(rad - nearest) < SNAP_PULL ? nearest : rad;
}

/** (Re)baseline the rotate gesture against whichever two pointers are
 *  currently down, so a 3rd finger touching or lifting never causes a jump —
 *  it just re-anchors from the current (already-applied) rotation. */
function syncRotateGesture(): void {
  if (!CANVAS_ROTATION_ENABLED) return;
  if (activePointers.size !== 2) {
    rotateGesture = null;
    return;
  }
  const [p0, p1] = [...pointerPositions.values()];
  if (!p0 || !p1) return;
  rotateGesture = {
    startAngle: Math.atan2(p1[1] - p0[1], p1[0] - p0[0]),
    startRotation: canvasStore.rotation,
  };
}

function updateRotateGesture(): void {
  if (!CANVAS_ROTATION_ENABLED || !rotateGesture) return;
  const [p0, p1] = [...pointerPositions.values()];
  if (!p0 || !p1) return;
  const angle = Math.atan2(p1[1] - p0[1], p1[0] - p0[0]);
  const raw = rotateGesture.startRotation + (angle - rotateGesture.startAngle);
  canvasStore.rotation = snapRotation(raw);
}

/**
 * Normalise a pointer sample's pressure/tilt for the drawing engine. Safari's
 * "no pressure sensor" sentinel is exactly 0.5 (finger/mouse/non-Pencil), so a
 * genuine reading is `pressure > 0 && pressure !== 0.5`. Tilt is 0 for anything
 * without a stylus, which the engine treats as an upright pencil.
 */
// Detects "pressure that never varies" (see penDynamics.ts doc comment) and
// falls back to the flat default weight rather than a stuck derived value.
// One tracker for the component's lifetime — effectively "per session."
const pressureReliability = createPressureReliabilityTracker();

function penInputFrom(ev: PointerEvent): PenInput {
  const pressure = ev.pressure;
  pressureReliability.observe(pressure);
  const hasPressure = pressure > 0 && pressure !== 0.5 && pressureReliability.isReliable();
  return {
    pressure,
    hasPressure,
    tiltMag: Math.hypot(ev.tiltX || 0, ev.tiltY || 0),
    pointerType: ev.pointerType as 'mouse' | 'touch' | 'pen',
  };
}
function onPointerDown(e: PointerEvent): void {
  if (e.button !== 0) return;
  if (e.shiftKey) return; // shift-drag = pan (d3-zoom)
  activePointers.add(e.pointerId);
  pointerPositions.set(e.pointerId, [e.offsetX, e.offsetY]);
  if (activePointers.size > 1) {
    if (gesture === 'pen') drawing.cancelStroke();
    // §6: a pinch's first finger already ran trace.onDown and dropped a node;
    // the arriving second finger means this is a zoom/rotate, not a tap — undo
    // that stray placement rather than leaving it behind.
    else if (gesture === 'trace') trace.cancelLastPlacement();
    gesture = null;
    if (!multiTap) {
      multiTap = { maxCount: activePointers.size, startTime: performance.now(), moved: false, starts: new Map(pointerPositions) };
    } else {
      multiTap.maxCount = Math.max(multiTap.maxCount, activePointers.size);
    }
    multiTap.starts.set(e.pointerId, [e.offsetX, e.offsetY]);
    syncRotateGesture();
    return;
  }
  try {
    canvasEl.value?.setPointerCapture(e.pointerId);
  } catch {
    // synthetic/stale pointers can't be captured — drawing works without it
  }
  canvasStore.pointerType = (e.pointerType as 'mouse' | 'touch' | 'pen') || 'mouse';
  canvasStore.pressureSupported = e.pressure > 0 && e.pressure !== 0.5;
  const [wx, wy] = screenToWorld(e.offsetX, e.offsetY);
  if (layers.activeLayer === 'trace') {
    gesture = 'trace';
    trace.onDown(wx, wy);
  } else if (tools.activeTool === 'fill') {
    drawing.fillAt(wx, wy);
    gesture = null;
  } else if (tools.activeTool === 'erase') {
    gesture = drawing.startErase(wx, wy) ? 'erase' : null;
  } else {
    gesture = drawing.startStroke(wx, wy, penInputFrom(e)) ? 'pen' : null;
  }
}

function onPointerMove(e: PointerEvent): void {
  if (pointerPositions.has(e.pointerId)) {
    pointerPositions.set(e.pointerId, [e.offsetX, e.offsetY]);
    if (activePointers.size === 2) updateRotateGesture();
  }
  if (multiTap) {
    const start = multiTap.starts.get(e.pointerId);
    if (start && Math.hypot(e.offsetX - start[0], e.offsetY - start[1]) > TAP_MAX_MOVE_PX) {
      multiTap.moved = true;
    }
  }
  if (layers.activeLayer === 'trace' && activePointers.size <= 1) {
    const [wx, wy] = screenToWorld(e.offsetX, e.offsetY);
    trace.onMove(wx, wy);
    return;
  }
  if (!gesture || activePointers.size !== 1 || !activePointers.has(e.pointerId)) return;
  const events = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [e];
  for (const ev of events.length > 0 ? events : [e]) {
    const [wx, wy] = screenToWorld(ev.offsetX, ev.offsetY);
    if (gesture === 'erase') drawing.continueErase(wx, wy);
    else drawing.continueStroke(wx, wy, penInputFrom(ev));
  }
}

function onPointerUp(e: PointerEvent): void {
  if (activePointers.has(e.pointerId) && activePointers.size === 1) {
    if (gesture === 'trace') trace.onUp();
    else if (gesture === 'erase') drawing.endErase();
    else if (gesture === 'pen') drawing.endStroke();
  }
  gesture = null;
  activePointers.delete(e.pointerId);
  pointerPositions.delete(e.pointerId);
  syncRotateGesture();
  finishMultiTap();
}

function onPointerCancel(e: PointerEvent): void {
  activePointers.delete(e.pointerId);
  pointerPositions.delete(e.pointerId);
  syncRotateGesture();
  finishMultiTap();
  if (gesture === 'trace') trace.onUp();
  else if (gesture === 'erase') drawing.endErase();
  else if (gesture === 'pen') drawing.cancelStroke();
  gesture = null;
}

/** Snap-highlight/rubber-band hover feedback (§ snap preview) tracks the
 *  pointer live via plain pointermove — mouse hover and Apple Pencil hover
 *  (no touch, no button held) both fire that without ever reaching
 *  pointerdown, so there's no matching "up"/"cancel" to clear it on. Without
 *  this, moving the mouse to a toolbar or lifting the pencil out of hover
 *  range leaves the highlight and rubber-band line stuck at the last
 *  position. Skipped mid-gesture (captured pointers don't fire `leave`
 *  until release in practice, but guard anyway) so an in-progress drag
 *  isn't disturbed. */
function onPointerLeave(): void {
  if (gesture) return;
  if (layers.activeLayer === 'trace') trace.onHoverEnd();
}

function onDblClick(): void {
  if (layers.activeLayer === 'trace' && trace.mode.value === 'draw') trace.close();
}

function onKeyDown(e: KeyboardEvent): void {
  if (layers.activeLayer !== 'trace') return;
  if (e.key === 'Escape') trace.cancel();
  else if (e.key === 'Enter') {
    if (trace.mode.value === 'draw') trace.close();
    else trace.commit();
  } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && trace.canUndoStep.value) {
    // §7: step-undo the last placed vertex(es) while drawing — trace-specific,
    // takes priority over the general canvas undo during an in-progress trace.
    e.preventDefault();
    trace.undoStep();
  }
}

// ---- viewport ----
let resizeObserver: ResizeObserver | null = null;
let resizeTimer: ReturnType<typeof setTimeout> | null = null;

// iPad / Capacitor rotation fires a burst of ResizeObserver callbacks as the
// viewport animates through intermediate sizes; re-rasterising the whole canvas
// on every one is the visible jank (brief 14-even_closer §iPad rotation).
// Coalesce the burst: measure once immediately (leading edge, so an ordinary
// single resize stays responsive) then once more after it settles, skipping the
// intermediate churn entirely.
function onResize(): void {
  if (resizeTimer === null) measure();
  else clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    resizeTimer = null;
    measure();
  }, 150);
}

function measure(): void {
  const wrapper = wrapperEl.value;
  const canvas = canvasEl.value;
  if (!wrapper || !canvas) return;
  dpr = window.devicePixelRatio || 1;
  const rect = wrapper.getBoundingClientRect();
  canvasStore.canvasWidth = rect.width;
  canvasStore.canvasHeight = rect.height;
  canvas.width = Math.round(rect.width * dpr);
  canvas.height = Math.round(rect.height * dpr);
  canvas.style.width = `${rect.width}px`;
  canvas.style.height = `${rect.height}px`;
  scheduleDraw();
}

onMounted(() => {
  measure();
  resizeObserver = new ResizeObserver(onResize);
  if (wrapperEl.value) resizeObserver.observe(wrapperEl.value);
  window.addEventListener('keydown', onKeyDown);

  const canvas = canvasEl.value!;
  const selection = select(canvas as Element);

  // Initial view: active tile centred with ~0.75 of a tile visible each side.
  const w = canvasStore.canvasWidth;
  const h = canvasStore.canvasHeight;
  const ts = patternStore.tileSize;
  const k0 = Math.min(w, h) / (ts * 2.0);

  // Feature 1 (canvas rotation) interaction: d3-zoom computes its transform
  // straight from raw screen-space finger/wheel movement, but the renderer
  // treats viewTransform.{x,y} as living in the UNROTATED frame — it rotates
  // that translation about the viewport centre afterward (see
  // useWrapTransform's rotateAboutCentre and draw()'s setPlacement above).
  // So a raw screen delta must be un-rotated (useWrapTransform's
  // unrotateScreenDelta) before it's folded into viewTransform, or
  // two-finger pan/pinch drifts off-axis by the rotation angle. d3-zoom keeps
  // its own internal transform (__zoom on the element) which we never touch
  // directly — `lastZoomTransform` just lets us diff consecutive events into
  // a delta; scaleExtent/translateExtent keep constraining d3's internal
  // state exactly as before.
  let lastZoomTransform: ZoomTransform = zoomIdentity;

  zoomMin.value = k0 * 0.4;
  const zoomBehavior = zoom()
    .scaleExtent([k0 * 0.4, MAX_ZOOM])
    .translateExtent([
      [-4 * ts, -4 * ts],
      [5 * ts, 5 * ts],
    ])
    .filter((e: Event & { touches?: TouchList; shiftKey?: boolean; ctrlKey?: boolean; metaKey?: boolean; type: string }) => {
      if (e.type === 'wheel') return Boolean(e.ctrlKey || e.metaKey);
      if (e.type.startsWith('touch')) return (e.touches?.length ?? 0) > 1;
      return Boolean(e.shiftKey);
    })
    .on('start', (e: D3ZoomEvent<Element, unknown>) => {
      // Re-baseline against whatever d3's transform already is, so this
      // gesture's first delta is against its own start, not a stale one left
      // over from a previous gesture/programmatic transform() call.
      lastZoomTransform = e.transform;
    })
    .on('zoom', (e: D3ZoomEvent<Element, unknown>) => {
      const dx = e.transform.x - lastZoomTransform.x;
      const dy = e.transform.y - lastZoomTransform.y;
      const [rdx, rdy] = unrotateScreenDelta(dx, dy);
      const prev = canvasStore.viewTransform;
      // Scale is not rotation-dependent — take it straight from d3.
      canvasStore.viewTransform = zoomIdentity.translate(prev.x + rdx, prev.y + rdy).scale(e.transform.k);
      lastZoomTransform = e.transform;
      canvasStore.viewMoved = true;
    });
  selection.call(zoomBehavior as never);
  selection.on('dblclick.zoom', null);

  // §1 reset-view: recompute the initial fit from current dims and re-apply it
  // through d3 (which owns the transform), snapping pan+zoom home; rotation is
  // eased back separately in the store's resetView().
  const applyFit = (): void => {
    const fw = canvasStore.canvasWidth;
    const fh = canvasStore.canvasHeight;
    const fts = patternStore.tileSize;
    const fk = Math.min(fw, fh) / (fts * 2.0);
    const fit = zoomIdentity.translate(fw / 2 - 0.5 * fts * fk, fh / 2 - 0.5 * fts * fk).scale(fk);
    zoomBehavior.transform(selection as never, fit);
  };
  watch(() => canvasStore.resetViewNonce, applyFit);

  // §6: the zoom slider drives d3 (the transform's owner) about the viewport
  // centre, so it stays in sync with pinch/wheel rather than fighting them.
  applyScaleTo = (k: number): void => {
    zoomBehavior.scaleTo(selection as never, k, [canvasStore.canvasWidth / 2, canvasStore.canvasHeight / 2]);
  };

  canvas.addEventListener(
    'wheel',
    (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      const k = canvasStore.viewTransform.k;
      zoomBehavior.translateBy(selection as never, -e.deltaX / k, -e.deltaY / k);
    },
    { passive: false },
  );

  // Bug 2 (mobile first-touch): `touch-action: none` on the canvas is what's
  // *supposed* to stop the browser/WKWebView from ever starting a native
  // pan/scroll/pinch for touches that land here, and d3-zoom's own filter
  // above already refuses to start a zoom gesture for a single finger
  // (traced through d3-zoom's touchstarted() in node_modules — it bails out
  // before doing anything if `touches.length` isn't > 1, so it was never the
  // thing consuming the tap; confirmed empirically too — a real WebKit
  // engine, given this exact filter, never emits a 'zoom' event for a
  // single-finger tap, and pointerdown fires before touchstart/mousedown for
  // touch input so the drawing handler already runs first). The actual gap
  // is that `touch-action` support/timing has known holes in WKWebView
  // specifically (as opposed to Safari's own browser chrome) — most visible
  // on the very first touch of a session, before the engine has committed
  // the CSS to its native scrolling/gesture regions — which can let iOS's
  // own scroll-view pan recogniser win that first gesture and pan the page
  // instead of the canvas ever seeing a clean drawing stroke.
  // preventDefault() on the touch events themselves is authoritative
  // regardless of that timing, so this enforces it explicitly rather than
  // trusting the CSS alone. Only applied to single-finger touches —
  // two-finger pinch/pan must still fall through to d3-zoom untouched.
  canvas.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      if (e.touches.length === 1) e.preventDefault();
    },
    { passive: false },
  );
  canvas.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      if (e.touches.length === 1) e.preventDefault();
    },
    { passive: false },
  );

  const initial = zoomIdentity.translate(w / 2 - 0.5 * ts * k0, h / 2 - 0.5 * ts * k0).scale(k0);
  zoomBehavior.transform(selection as never, initial);

  renderLoop();
});

onBeforeUnmount(() => {
  cancelAnimationFrame(rafId);
  if (timerId) clearTimeout(timerId);
  if (resizeTimer) clearTimeout(resizeTimer);
  resizeObserver?.disconnect();
  window.removeEventListener('keydown', onKeyDown);
});

// redraw whenever anything visual changes
watchEffect(() => {
  void canvasStore.viewTransform;
  void canvasStore.rotation;
  void patternStore.currentPatternId;
  void patternStore.currentVariantId;
  // lattice-param tweaks keep the same shape+wpg ids but rebuild the config —
  // track it so adjusting sliders redraws live.
  void patternStore.currentConfig;
  void patternStore.tileSize;
  void layers.revision;
  // Track every raster layer (sketch + ink) so a visibility/opacity toggle on
  // either redraws live.
  for (const l of layers.rasterLayers) {
    void l.opacity;
    void l.visible;
  }
  void layers.activeLayer;
  void layers.activeInkLayerId;
  void layers.traceLayers.length;
  void layers.editingTraceLayerId;
  for (const l of layers.traceLayers) {
    void l.opacity;
    void l.visible;
  }
  void tools.showGhosts;
  void tools.ghostOpacity;
  void tools.colourCount;
  void tools.colours;
  void ui.theme;
  void drawing.gestureTick.value;
  void drawing.drawingActive.value;
  void trace.tick.value;
  void canvasStore.canvasWidth;
  void canvasStore.canvasHeight;
  scheduleDraw();
});

// drop stale Path2D cache entries when trace layers change
watch(
  () => layers.traceLayers.length,
  () => {
    const ids = new Set(layers.traceLayers.map((l) => l.id));
    for (const key of path2dCache.keys()) {
      if (!ids.has(key)) path2dCache.delete(key);
    }
  },
);

watch(
  () => patternStore.currentPatternId,
  () => {
    drawing.cancelStroke();
    trace.reset();
  },
);
</script>

<template>
  <div ref="wrapperEl" class="tile-canvas-wrapper">
    <canvas
      ref="canvasEl"
      class="tile-canvas"
      :class="{ inking: layers.activeLayer === 'trace' }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerCancel"
      @pointerleave="onPointerLeave"
      @dblclick="onDblClick"
    />
    <!-- §6: draggable zoom control, left edge (a safe-zone control clear of the
         canvas working area — §7). Drives d3 zoom, log-scaled. -->
    <input
      class="zoom-slider"
      type="range"
      min="0"
      max="1"
      step="0.005"
      v-model.number="zoomPct"
      aria-label="Zoom"
      title="Zoom"
    />
    <TraceFabs />
  </div>
</template>

<style scoped>
.tile-canvas-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.tile-canvas {
  display: block;
  touch-action: none;
  cursor: crosshair;
}

.tile-canvas.inking {
  cursor: default;
}

/* §6 zoom control — vertical slider hugging the left edge, top = most zoomed. */
.zoom-slider {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  height: min(45%, 260px);
  width: 28px;
  writing-mode: vertical-lr;
  direction: rtl;
  -webkit-appearance: slider-vertical;
  appearance: slider-vertical;
  accent-color: var(--accent);
  cursor: pointer;
  opacity: 0.65;
  z-index: 5;
}

.zoom-slider:hover {
  opacity: 1;
}
</style>
